import { intensiveQueryOptions } from "@/admin/intensiveQueryOptions.ts";
import { adminMutation } from "@/admin/utils/adminMutation.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { atomFamilyMulti } from "@/utils/jotai/atomFamilyMulti.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import type { QueryKey } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import type {
    AdminUpdateUserFlagBody,
    AdminUserMfaResponse,
    AdminUserResetMfaBody,
    UserSignups,
} from "@warrenio/api-spec/spec.oats.gen";
import { atomFamily } from "jotai-family";

//#region Query and parameters
interface Params {
    limit_months: number;
}

function getUserSignupsQueryKey(params?: Params): QueryKey {
    return params == null
        ? ["user-resource/admin/user_signups"]
        : ["user-resource/admin/user_signups", params.limit_months];
}

function getUserSignupsQuery(client: ApiClient, params: Params) {
    return queryOptions({
        ...intensiveQueryOptions,
        queryKey: getUserSignupsQueryKey(params),
        queryFn: async ({ signal }): Promise<UserSignups> => {
            const { limit_months } = params;
            return getResponseData(
                await client.GET("/user-resource/admin/user_signups", {
                    signal,
                    params: { query: { limit_months } },
                }),
            );
        },
    });
}

function getMfaQueryKey({ user_id }: UserMfaStatusParams): QueryKey {
    return ["admin_user_mfa_is_active", user_id];
}

interface UserMfaStatusParams {
    user_id: number;
}

function userMfaStatusForAdminQuery(apiClient: ApiClient, params: UserMfaStatusParams) {
    const { user_id } = params;
    const mfaQueryKey = getMfaQueryKey({ user_id });
    return queryOptions({
        queryKey: mfaQueryKey,
        queryFn: async ({ signal }): Promise<AdminUserMfaResponse> => {
            return getResponseData(
                await apiClient.GET("/user-resource/admin/mfa/is_active", {
                    signal,
                    params: { query: { user_id } },
                }),
            );
        },
    });
}

export const userMfaStatusForAdminQueryAtom = atomFamily((user_id: number) =>
    atomFromStandardQueryOptions(userMfaStatusForAdminQuery, { user_id }),
);
//#endregion

//#region Mutations
const entity = "User";

export const resetUserMfaMutation = adminMutation((apiClient) => ({
    entity,
    operation: "reset MFA",
    requestFn: async ({ user_id }: AdminUserResetMfaBody) =>
        await apiClient.POST("/user-resource/admin/mfa/reset", {
            ...jsonEncodedBody,
            body: { user_id },
        }),
    invalidateQueryKey: ({ user_id }: AdminUserResetMfaBody) => getMfaQueryKey({ user_id }),
}));

export const updateUserInvoiceFlagMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ body }: { body: AdminUpdateUserFlagBody }) =>
        await apiClient.PATCH("/user-resource/admin/user/flags", {
            ...jsonEncodedBody,
            body,
        }),
}));
//#endregion

//#region Atoms
export const userSignupsQueryAtom = atomFamilyMulti((limit_months: number) =>
    atomFromStandardQueryOptions(getUserSignupsQuery, { limit_months }),
);
//#endregion
